// Styles
import '../../MapStyles/MapMenu.scss';
import '../../MapStyles/MapEdit.scss';

// React
import { useEffect, useRef, useState } from 'react';

// React Bootstrap
import { Card, Form, InputGroup } from 'react-bootstrap';
import { AsyncTypeahead, Highlighter } from 'react-bootstrap-typeahead';

// Services
import { Api } from '@services';

// Third-party libraries
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

// Components
import { Icon, Text } from '@components';

// Store
import { useMapStore } from '../../MapStore/UseMapStore';

export function MapCreateGateway({
  curGateways,
  selectedGateway,
  setCurGateways,
  setSelectedGateway,
  setIsCreating,
}) {
  const setNewObjectId = useMapStore((state) => state.setNewObjId);
  const setNewObjName = useMapStore((state) => state.setNewObjName);
  const setNewObjFloor = useMapStore((state) => state.setNewObjFloor);
  const setNewObjKeywords = useMapStore((state) => state.setNewObjKeywords);
  const setNewObjOnline = useMapStore((state) => state.setNewObjOnline);
  const setCreateGateway = useMapStore((state) => state.setCreateGateway);
  const addObject = useMapStore((state) => state.addObject);
  const removeTempObject = useMapStore((state) => state.removeTempObject);
  const selected = useMapStore((state) => state.selected);
  const newObjCreated = useMapStore((state) => state.newObjCreated);

  const [name, setName] = useState<string>('');
  const [floor, setFloor] = useState<string>('1');
  const [id, setId] = useState<string>(null);
  const [isOnline, setIsOnline] = useState<boolean>(null);
  const [showSave, setShowSave] = useState<boolean>(false);
  const [keywords, setKeywords] = useState<any>([]);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [cache, setCache] = useState({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<any[]>([]);
  const [query, setQuery] = useState<string>('');
  const [filterAssigned, setFilterAssigned] = useState(false);
  const gatewayRef = useRef(null);
  const floorRef = useRef(null);

  const MySwal = withReactContent(Swal);

  useEffect(() => {
    setNewObjectId(id);
  }, [id, setNewObjectId]);

  useEffect(() => {
    setNewObjName(name);
  }, [name, setNewObjName]);

  useEffect(() => {
    setNewObjFloor(floor);
  }, [floor, setNewObjFloor]);

  useEffect(() => {
    setNewObjKeywords(keywords);
  }, [keywords, setNewObjKeywords]);

  useEffect(() => {
    setNewObjOnline(isOnline);
  }, [isOnline, setNewObjOnline]);

  useEffect(() => {
    newObjCreated !== null ? setShowSave(true) : setShowSave(false);
  }, [newObjCreated]);

  const handleInput = (items) => {
    if (items.length > 0) {
      if (items[0].assigned) {
        MySwal.fire({
          allowOutsideClick: false,
          title: 'You are reassigning this Gateway',
          text: 'Are you sure you want to remove this Gateway from its current map?',
          icon: 'warning',
          showCancelButton: true,
          cancelButtonText: 'No',
          confirmButtonText: 'Yes',
        }).then((submit) => {
          if (submit.isConfirmed) {
            setSelectedGateway(items[0].id);
            setId(items[0].id.toString());
            setName(items[0].label);
            setIsOnline(items[0].isOnline);
            setIsInvalid(false);
            setTimeout(() => {
              gatewayRef.current.blur();
            }, 500);
          } else {
            MySwal.close();
            gatewayRef.current.clear();
          }
        });
      } else {
        setSelectedGateway(items[0].id);
        setId(items[0].id.toString());
        setName(items[0].label);
        setIsOnline(items[0].isOnline);
        setIsInvalid(false);
      }
    } else {
      setSelectedGateway(null);
      setId('');
      setName('');
      setIsOnline(null);
      setIsInvalid(true);
    }
  };

  const isAllowedKey = (key: string): boolean => {
    return ['ArrowLeft', 'ArrowRight', 'Backspace', 'Delete', 'Tab'].includes(key);
  };

  const isDigit = (key: string): boolean => /^\d$/.test(key);

  const isValidFloorInput = (value: string): boolean => {
    return (
      /^\d+$/.test(value) && // only digits
      (value === '0' || !value.startsWith('0')) // allow "0", disallow leading zeros
    );
  };

  function startDraw() {
    setCreateGateway(true);
  }


  const getResults = async (search: string, pageNumber: number, pageSize: number = 30) => {
    const call = `v2/gateways?search=${search}&pageSize=${pageSize}&pageNumber=${pageNumber}`;
    const resp = await Api.get<any[]>(call);

    const newItems = resp.data['data'].map((i) => ({
      id: i.id,
      label: i.name,
      isOnline: i.isOnline,
      deviceId: i.deviceId || '',
      disabled: curGateways.includes(i.id),
      assignedCur: curGateways.includes(i.id),
      assigned: i.assignedMap ? i.assignedMap.id : null,
    }));

    const next = resp.data['hasNextPage'];
    const total = resp.data['totalCount'];

    return { newItems, next, total, pageNumber };
  };

  const handleSearch = (value) => {
    setQuery(value);

    if (cache[value]) {
      setOptions(
        filterAssigned
          ? cache[value].items.filter((asset) => asset.assigned === null)
          : cache[value].items,
      );
    } else {
      setIsLoading(true);
      getResults(value, 1).then((resp) => {
        setOptions(
          filterAssigned
            ? resp.newItems.filter((asset) => asset.assigned === null)
            : resp.newItems,
        );
        setCache({
          ...cache,
          [value]: {
            items: resp.newItems,
            next: resp.next,
            total: resp.total,
            pageNumber: resp.pageNumber,
          },
        });
        setIsLoading(false);
      });
    }
  };

  const handlePagination = () => {
    const cached = cache[query];

    if (cached && cached.items.length !== cached.total) {
      setIsLoading(true);
      const nextPage = cached.pageNumber + 1;

      getResults(query, nextPage).then((resp) => {
        const items = cached.items.concat(resp.newItems);

        setOptions(
          filterAssigned
            ? items.filter((asset) => asset.assigned === null)
            : items,
        );
        setCache({
          ...cache,
          [query]: {
            items,
            next: resp.next,
            total: resp.total,
            pageNumber: nextPage,
          },
        });
        setIsLoading(false);
      });
    }
  };

  const handleClick = () => {
    setCurGateways([...curGateways, selectedGateway]);
    addObject('Gateway');
    removeTempObject();
    setIsCreating(false);
    setCreateGateway(false);
    setShowSave(false);
  };

  return (
    <Form className={selected !== 'gateway' ? 'object-wrapper' : 'object-wrapper-expand'}>
      <Card.Body>
        <div className="d-flex flex-column justify-content-between">
          <div className="d-flex flex-row align-items-center justify-content-between">
            <Text className="fw-semibold fs-md">Gateway Search:</Text>
            <div>
              {showSave === true ? (
                <Icon
                  name={'check'}
                  title="Confirm"
                  className="align-sub primary-icon"
                  variant="light"
                  disabled={name === ''}
                  size="md"
                  onClick={handleClick}
                />
              ) : (
                <Icon
                  name={'pin-edit'}
                  title="Place Gateway"
                  className="align-sub primary-icon"
                  variant="light"
                  disabled={name === ''}
                  size="md"
                  onClick={startDraw}
                />
              )}

              <Icon
                name={'add'}
                title="Close"
                className="sonarIcon-rotate-45 align-sub nav-text-icon"
                variant="light"
                disabled={false}
                size="md"
                onClick={() => {
                  setCreateGateway(false);
                  setIsCreating(false);
                  removeTempObject();
                }}
              />
            </div>
          </div>
          <div className="d-flex flex-column align-items-left justify-content-between mb-3 qw-100">
            <AsyncTypeahead
              className="mb-3 w-100"
              ref={gatewayRef}
              filterBy={() => true}
              delay={500}
              id="gateway"
              isInvalid={isInvalid}
              isLoading={isLoading}
              maxResults={10}
              minLength={1}
              onChange={(e) => handleInput(e)}
              onSearch={handleSearch}
              onPaginate={handlePagination}
              options={options || []}
              paginate
              placeholder="Search for a Gateway..."
              renderMenuItemChildren={(option: any, { text }) => (
                <div
                  title={option.label}
                  style={{ opacity: option.assignedCur ? 0.5 : 1 }}
                >
                  <Highlighter search={text}>{option.label}</Highlighter>
                  <div className="d-flex flex-column">
                    <Text className="fs-xs">ID: {option.id}</Text>
                    <Text className="fs-xs">
                      Online:{' '}
                      {option.isOnline.toString().charAt(0).toUpperCase() +
                        option.isOnline.toString().slice(1)}
                    </Text>
                    {option.assigned && (
                      <div className="d-flex flex-row">
                        <Text className="fs-xs warn-text">Assigned to Map: &nbsp;</Text>
                        <Text className="fs-xs primary-text">{option.assigned}</Text>
                      </div>
                    )}
                    {option.assignedCur && (
                      <Text className="fs-xs">{'(Gateway is already assigned to this Map)'}</Text>
                    )}
                  </div>
                </div>
              )}
              useCache={false}
            />
            <Form.Check
              label="Filter Assigned Gateways"
              id={'filter-assigned-beacons-checkbox'}
              checked={filterAssigned}
              onChange={(e) => setFilterAssigned(e.target.checked)}
            />
          </div>
          <div className="d-flex flex-column justify-content-between mt-3">
            <Text className="fw-semibold fs-md mb-3">Floors: </Text>
            <InputGroup className="me-2">
              <Form.Control
                ref={floorRef}
                aria-label="Floor"
                type="text"
                inputMode="numeric"
                required
                value={floor}
                onKeyDown={(e) => {
                  const { key, currentTarget } = e;
                  const { value } = currentTarget;

                  if (isAllowedKey(key)) return;

                  if (!isDigit(key)) {
                    e.preventDefault();
                    return;
                  }

                  if (value === '' && key === '0') return;

                  if (value === '0') {
                    e.preventDefault();
                    return;
                  }

                  if (value.length === 1 && value.startsWith('0')) {
                    e.preventDefault();
                    return;
                  }
                }}
                onChange={(e) => {
                  const raw = e.target.value;

                  if (raw === '') {
                    setFloor('');
                    return;
                  }

                  if (isValidFloorInput(raw)) {
                    setFloor(raw);
                  }
                }}
              />
            </InputGroup>
          </div>
        </div>
      </Card.Body>
    </Form>
  );
}